/**
 * Swipe a bottom sheet away, with a little give.
 *
 * One gesture for every sheet: the More sheet and the Hater Wire's feed share it, so a sheet that
 * follows the finger on one screen does not sit like a rock on another. The rules are pure and
 * tested in `sheetGesture.ts`; this is the wiring: pointer events, the transform, and the snap
 * back. On a desktop the same markup is a centred dialog positioned by its own transform, so the
 * gesture is off there and the style is left alone.
 */

import { useRef, useState, type CSSProperties, type PointerEvent as RPointerEvent } from 'react';
import { followY, ownsDrag, shouldDismiss } from './sheetGesture.js';

const PHONE = '(max-width: 899px)';

export function useSheetSwipe(onClose: () => void): {
  style: CSSProperties | undefined;
  handlers: {
    onPointerDown: (e: RPointerEvent) => void;
    onPointerMove: (e: RPointerEvent) => void;
    onPointerUp: (e: RPointerEvent) => void;
    onPointerCancel: (e: RPointerEvent) => void;
  };
} {
  const [dragY, setDragY] = useState(0);
  /* True for one frame after release, so the snap back animates and a fresh drag does not. */
  const [settling, setSettling] = useState(false);
  const from = useRef<{ y: number; at: number } | null>(null);

  const onPointerDown = (e: RPointerEvent) => {
    if (typeof window !== 'undefined' && !window.matchMedia(PHONE).matches) return;
    /* A drag that starts inside a list that is scrolled is the list's; see `ownsDrag`. */
    const path: Array<{ overflowY: string; scrollHeight: number; clientHeight: number; scrollTop: number }> = [];
    for (let el = e.target as HTMLElement | null; el; el = el.parentElement) {
      path.push({
        overflowY: getComputedStyle(el).overflowY, scrollHeight: el.scrollHeight,
        clientHeight: el.clientHeight, scrollTop: el.scrollTop,
      });
      if (el === e.currentTarget) break;
    }
    if (ownsDrag(path)) return;
    from.current = { y: e.clientY, at: e.timeStamp };
    setSettling(false);
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: RPointerEvent) => {
    if (!from.current) return;
    setDragY(followY(e.clientY - from.current.y));
  };

  const onPointerUp = (e: RPointerEvent) => {
    const start = from.current;
    from.current = null;
    setDragY(0);
    setSettling(true);
    if (!start) return;
    if (shouldDismiss(e.clientY - start.y, e.timeStamp - start.at)) onClose();
  };

  const style: CSSProperties | undefined = dragY
    ? { transform: `translateY(${dragY}px)`, transition: 'none' }
    : settling
      ? { transform: 'translateY(0)', transition: 'transform .22s cubic-bezier(.2,.8,.2,1)' }
      : undefined;

  return {
    style,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp },
  };
}
